/**
 * Table row for a subject.
 */
import * as React from 'react';
import { Star } from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { LabelBadge } from '@/components/common/LabelBadge';
import { TableRow } from '@/components/common/data-table';
import { SUBJECT_STATUS_CONFIG, getEducationCycleLabelFr } from './subject-configs';
import { getSubjectCycleConfig } from './subject-table-utils';
import type { Subject } from '@/types/subject.types';

/** Props for {@link SubjectRow}. */
interface SubjectRowProps {
  subject: Subject;
  onView?: (id: string) => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  pageActions?: { canEditRow?: boolean; canDeleteRow?: boolean };
}

/** Table row for a subject. */
export const SubjectRow: React.FC<SubjectRowProps> = ({
  subject,
  onView,
  onEdit,
  onDelete,
  pageActions,
}) => {
  const canEdit = pageActions?.canEditRow ?? true;
  const canDelete = pageActions?.canDeleteRow ?? true;
  const statusConfig = SUBJECT_STATUS_CONFIG[subject.status];
  const cycleConfig = getSubjectCycleConfig(subject);
  const StatusIcon = statusConfig.icon;
  const CycleIcon = cycleConfig?.icon;

  let cycleCell: React.ReactNode = null;
  if (cycleConfig && CycleIcon) {
    cycleCell = (
      <LabelBadge
        label={cycleConfig.label}
        icon={CycleIcon}
        variant={cycleConfig.badgeVariant}
        color={cycleConfig.badgeColor}
        size="md"
      />
    );
  } else if (subject.educationCycle) {
    cycleCell = (
      <span className="text-sm text-foreground">
        {getEducationCycleLabelFr(subject.educationCycle)}
      </span>
    );
  }

  return (
    <TableRow>
      <td>
        <div className="flex items-center gap-2">
          <span className="font-medium text-foreground">{subject.code}</span>
        </div>
      </td>

      <td>
        <span className="text-foreground">{subject.label}</span>
      </td>

      <td>{cycleCell}</td>

      <td>
        {subject.isCore && (
          <LabelBadge
            label="Matière principale"
            icon={Star}
            variant="outlined"
            color="var(--color-2-nuance-1)"
            size="sm"
          />
        )}
      </td>

      <td>
        <LabelBadge
          label={statusConfig.label}
          icon={StatusIcon}
          variant={statusConfig.badgeVariant}
          color={statusConfig.badgeColor}
          size="md"
        />
      </td>

      <td className="text-center">
        <div className="flex items-center justify-center gap-1">
          <Button preset="row-view" onClick={() => onView?.(subject.id)} />

          {canEdit && <Button preset="row-edit" onClick={() => onEdit?.(subject.id)} />}

          {canDelete && <Button preset="row-delete" onClick={() => onDelete?.(subject.id)} />}
        </div>
      </td>
    </TableRow>
  );
};
